import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { PowerBIEmbed } from 'powerbi-client-react';
import * as PowerBI from 'powerbi-client';
import * as Sentry from '@sentry/react';
import { IReportEmbedConfiguration, Embed, Report } from 'powerbi-client';
import { FilterType } from 'powerbi-models';

import { ApplicationState } from 'store/redux';
import * as AuthenticationStore from 'store/redux/Authentication';
import { IAuthenticationActionCreators, UserState } from 'store/redux/Authentication';
import Context from 'configuration/context';
import { IReportContext, powerBiServiceFactory } from 'services/powerBIService';
import { authenticationServiceFactory } from 'services/authenticationService';
import * as HttpErrors from 'services/httpErrors';
import './Statistics.css';

export type IStatisticsProps = IAuthenticationActionCreators & {
  authenticationState?: UserState;
};

const Statistics: React.FunctionComponent<React.PropsWithChildren<IStatisticsProps>> = ({
  authenticationState,
  setLoginState,
}: React.PropsWithChildren<IStatisticsProps>) => {
  const { t } = useTranslation('translation');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [context, setContext] = useState<IReportContext>();
  const [report, setReport] = useState<Report>();
  const LoginState = authenticationState?.LoginState;
  const customerId = authenticationState?.UserInRole?.selectedCustomer?.customerId;

  useEffect(() => {
    // Ignore responses from requests superseded by a newer customer/login state
    let cancelled = false;
    setError('');
    setMessage('');
    if (LoginState && LoginState === AuthenticationStore.LoginState.LOGGED_IN && customerId) {
      setIsLoading(true);
      powerBiServiceFactory(
        Context.APIUrl + '/v1',
        authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
      )
        .loadContext(customerId)
        .then((reportContext: IReportContext) => {
          if (cancelled) return;
          setContext(reportContext);
          setIsLoading(false);
        })
        .catch((error: any) => {
          if (cancelled) return;
          setContext(undefined);
          if (error === HttpErrors.FORBIDDEN) {
            setMessage('report.REPORT_UNAVAILABLE');
          } else {
            Sentry.captureException(error);
            setError('report.LOAD_FAILED');
          }
          setIsLoading(false);
        });
    } else {
      // Nothing to load without a logged-in user and selected customer
      setContext(undefined);
      setIsLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [LoginState, customerId]);

  useEffect(() => {
    if (context) {
      setIsLoading(false);
    }
  }, [context]);

  let filters: PowerBI.models.ReportLevelFilters[] = [];

  filters =
    customerId === '1000000001'
      ? []
      : [
          {
            $schema: 'http://powerbi.com/product/schema#advanced',
            filterType: FilterType.Advanced,
            target: {
              table: 'dim_date',
              column: 'date_filter',
            },
            logicalOperator: 'And',
            conditions: [{ operator: 'Is', value: '1' }],
          },
          {
            $schema: 'http://powerbi.com/product/schema#advanced',
            filterType: FilterType.Advanced,
            target: {
              table: 'dim_service',
              column: 'booking_type_name_sv',
            },
            logicalOperator: 'Or',
            conditions: [
              { operator: 'Is', value: 'Hållbar' },
              { operator: 'Is', value: 'Hälsa' },
              { operator: 'Is', value: 'Hälsorisk' },
              { operator: 'Is', value: 'Ohälsa' },
            ],
          },
          {
            $schema: 'http://powerbi.com/product/schema#advanced',
            filterType: FilterType.Advanced,
            target: {
              table: 'dim_customer',
              column: 'corporate_customer_code',
            },
            logicalOperator: 'And',
            conditions: [
              {
                operator: 'Is',
                value:
                  customerId && String(customerId) === '1000000001'
                    ? '1000000001-j4'
                    : `${context?.internalCustomerId}-j4`,
              },
            ],
          },
        ];

  const ReportConfig: IReportEmbedConfiguration = {
    type: 'report', // Supported types: report, dashboard, tile, visual and qna
    id: context?.report.id,
    embedUrl: context?.report.embedUrl,
    accessToken: context?.accessToken,
    tokenType: PowerBI.models.TokenType.Embed,
    filters: filters,
    settings: {
      localeSettings: {
        language: 'en-US',
      },
      navContentPaneEnabled: false,
      panes: {
        filters: {
          expanded: false,
          visible: false,
        },
      },
      background: PowerBI.models.BackgroundType.Transparent,
    },
  };

  var resetReport = () => {
    if (!report) return;
    report.on('rendered', afterInitialRender);
    report.reload();
  };

  const reportStartLevel: number = 1;
  const reportDepth: number = 5;

  var afterInitialRender = async (event: any) => {
    if (!report) return;

    report.off('rendered');
    const pages = await report.getPages();
    var cloneTitle = 'Clone' + reportStartLevel + '_' + (reportStartLevel + reportDepth - 1);
    var clonePage: PowerBI.Page | null = null;
    pages.forEach(function (e) {
      if (e.displayName === 'Kloner') {
        clonePage = e;
      }
    });
    if (clonePage != null) {
      const visuals = await (clonePage as PowerBI.Page).getVisuals();

      for (var i = 0; i < visuals.length; i++) {
        if (visuals[i].title === cloneTitle) {
          var visual = visuals[i];
          let cloneRequest = {
            layout: { x: 15, y: 490, z: 100000 },
          };
          try {
            await visual.clone(cloneRequest);
          } catch (error) {
            Sentry.captureException(error);
            console.error(error);
            alert('Loading the report failed!');
          }
          break;
        }
      }
    }
  };

  const afterPageChange = async (event: any) => {
    if (!event) return;
    const eventReport = event.detail.newPage.report as PowerBI.Report;
    await eventReport.updateFilters(PowerBI.models.FiltersOperations.ReplaceAll, filters);
  };

  return (
    <div>
      {isLoading ? (
        <h1 className="loading-indicator-container col-12">
          <Spinner />
          {t('report.INITIALIZING')}...
        </h1>
      ) : (
        <>
          {context && (
            <>
              <Alert color="info" className="text-center">
                {t('report.NEW_REPORT_AVAILABLE')}
              </Alert>
              <PowerBIEmbed
                cssClassName="power-bi-container"
                embedConfig={ReportConfig}
                eventHandlers={
                  new Map([
                    ['rendered', afterInitialRender],
                    ['pageChanged', afterPageChange],
                    [
                      'error',
                      (event: any) => {
                        console.debug(event.detail);
                      },
                    ],
                  ])
                }
                getEmbeddedComponent={(embeddedReport: Embed) => {
                  setReport(embeddedReport as Report);
                }}
              />
              <div className="mt-4 col-12">
                <button
                  onClick={() => {
                    report?.print();
                  }}
                  className="btn-primary print-button"
                >
                  <i className="fa fa-print" /> {t('report.PRINT')}
                </button>{' '}
                <button onClick={resetReport} className="btn-primary print-button">
                  <i className="fa fa-refresh" /> {t('report.RESET_REPORT')}
                </button>
              </div>
            </>
          )}
          {message && <div className="mt-4 col-12 text-center">{t(message)}</div>}
          {error && <div className="error-message">{t(error)}</div>}
        </>
      )}
    </div>
  );
};

export default connect(
  (appState: ApplicationState) => ({ authenticationState: appState.authentication }),
  { ...AuthenticationStore.actions }
)(Statistics);
